import React, { useState } from 'react';
import RemoveFromSectionMenuItem from './RemoveFromSectionMenuItem';
import * as styles from './SidebarItems.module.scss';
import SidebarRow, { SidebarRowRightContent } from './SidebarRow';
import SidebarSectionAnimation from './SidebarSectionAnimation';
import sortSidebarItems from './sortSidebarItems';
import TopicItemMenuItems from './TopicItemMenuItems';
import useAppSelector from '../app/redux/useAppSelector';
import { MenuItem, MenuSeparator } from '../components/Menu';
import { BEACON_TOPIC_SIDEBAR } from '../constants/onboarding';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { PostType } from '../graphql/generated/graphql';
import drillDownIcon from '../icons/non-prefilled/16x16/drillDown.svg';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import topicIcon from '../icons/prefilled/16x16/topic.svg';
import TutorialBeacon from '../onboarding/tutorial/TutorialBeacon';
import { getPostIcon, getPostState } from '../post/PostIcon';
import PostTitleWithIcons from '../post/PostTitleWithIcons';
import TopicNameWithIcon from '../topic/TopicNameWithIcon';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import sortTopic from '../topic/utils/sortTopic';
import postPath, { createPostPath } from '../utils/url/postPath';
import topicPath from '../utils/url/topicPath';

export const POST_FRAGMENT = gql(`
  fragment SidebarItems_post on SlimPostFields {
    id
    type
    ...PostTitleWithIcons_post
    ...getPostIcon_post
  }
`);

type PostFragment = DocumentType<typeof POST_FRAGMENT>;

interface SidebarItemsProps {
  activeTopics: Record<string, boolean>;
  expandedTopics?: Record<string, boolean>;
  posts?: PostFragment[];
  topics: AllTopicsTreeListTopic[];
  orderIds?: string[];
  sectionId: string;
}

type RenderItem =
  | (AllTopicsTreeListTopic & { sidebarType: 'topic' })
  | (PostFragment & { sidebarType: 'post' });

const SidebarItems = ({
  activeTopics,
  expandedTopics,
  posts = [],
  topics,
  orderIds,
  sectionId,
}: SidebarItemsProps) => {
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const [forcedExpandedKeys, setForceExpandedKeys] = useState<
    Record<string, boolean>
  >({});

  const combined: RenderItem[] = [
    ...topics.map((topic) => ({ ...topic, sidebarType: 'topic' as const })),
    ...posts.map((post) => ({ ...post, sidebarType: 'post' as const })),
  ];

  const items = Array.isArray(orderIds)
    ? sortSidebarItems(combined, orderIds)
    : combined;

  const renderTopic = (topic: AllTopicsTreeListTopic, path: string[]) => {
    const { childTopics, id } = topic;
    const key = [...path, id].join('.');
    const isForceExpanded = !expandedTopics || forcedExpandedKeys[key];
    const isExpanded =
      isForceExpanded !== undefined
        ? isForceExpanded
        : expandedTopics?.[id] || activeTopics[id];

    const isActive = activeTopics[id];

    const childPath = [...path, id];
    const childComponent =
      isExpanded && childTopics.length > 0 ? (
        <ul className={styles.list}>
          {childTopics
            .toSorted(sortTopic)
            .map((t) => renderTopic(t, childPath))}
        </ul>
      ) : null;

    const getExpandState = () => {
      if (!expandedTopics) return 'none';
      if (childTopics.length === 0) return 'invisible';
      return isExpanded ? 'expanded' : 'collapsed';
    };

    return (
      <TutorialBeacon
        key={key}
        id={BEACON_TOPIC_SIDEBAR}
        hide={!isActive}
        placement="right"
      >
        {(beaconProps) => (
          <li {...beaconProps} aria-label={topic.name}>
            <SidebarRow
              subItem
              level={path.length}
              icon={path.length === 0 ? topicIcon : null}
              to={topicPath(topic)}
              type="topic"
              onClick={() => {
                if (selectedSectionId != null) {
                  setForceExpandedKeys({
                    ...forcedExpandedKeys,
                    [key]: !isExpanded,
                  });
                }
              }}
              rightContent={
                <SidebarRowRightContent
                  menuItems={
                    <TopicItemMenuItems topic={topic} sectionId={sectionId} />
                  }
                  expandState={getExpandState()}
                  onExpandClick={() =>
                    setForceExpandedKeys({
                      ...forcedExpandedKeys,
                      [key]: !isExpanded,
                    })
                  }
                />
              }
              active={isActive}
            >
              <TopicNameWithIcon
                topic={topic}
                iconSize="regular"
                truncate="multi"
              />
            </SidebarRow>
            <SidebarSectionAnimation sectionKey={id} expanded={!!isExpanded}>
              {childComponent}
            </SidebarSectionAnimation>
          </li>
        )}
      </TutorialBeacon>
    );
  };

  const renderPost = (post: PostFragment) => {
    const postState = getPostState(post);

    return (
      <li key={post.id}>
        <SidebarRow
          subItem
          icon={getPostIcon(post, !postState.draft)}
          to={postPath(post)}
          type={post.type === PostType.Template ? 'template' : 'post'}
          rightContent={
            <SidebarRowRightContent
              menuItems={
                post.type === PostType.Template ? (
                  <>
                    <MenuItem
                      icon={postIcon}
                      to={createPostPath({ templateId: post.id })}
                    >
                      Use template
                    </MenuItem>
                    <RemoveFromSectionMenuItem
                      sectionId={sectionId}
                      itemId={post.id}
                    />
                    <MenuSeparator />
                    <MenuItem icon={drillDownIcon} to={postPath(post)}>
                      View template
                    </MenuItem>
                  </>
                ) : (
                  <>
                    <RemoveFromSectionMenuItem
                      sectionId={sectionId}
                      itemId={post.id}
                    />
                    <MenuSeparator />
                    <MenuItem icon={drillDownIcon} to={postPath(post)}>
                      View post
                    </MenuItem>
                  </>
                )
              }
              expandState={expandedTopics ? 'invisible' : 'none'}
            />
          }
        >
          <PostTitleWithIcons
            post={post}
            truncate="multi"
            iconSize="small"
            showVerifiedIcon={false}
            publicIconMuted
          />
        </SidebarRow>
      </li>
    );
  };

  return (
    <ul className={styles.list}>
      {items.map((item) => {
        if (item.sidebarType === 'post') {
          return renderPost(item);
        }
        if (item.sidebarType === 'topic') {
          return renderTopic(item, []);
        }
        return null;
      })}
    </ul>
  );
};

export default SidebarItems;
